import React, { useMemo } from "react";
import { Navigate } from "react-router-dom";

import { env } from "~/env";
import { ROUTES } from "~/router";
import { BOOKING_FLOW_EVENTS, useUpdateBookingFlowMutation } from "~/services";
import { NIRVANA_STATUS } from "~/services/nirvana/types";
import { useUsers } from "~/shared";
import { useAuthStore, useBookingFlowStore, usePatientStore } from "~/stores";
import { useNirvanaStore } from "~/stores/use-nirvana-store";
import {
  buildInitialVisitSchema,
  PersonalInformationFormData,
} from "../validation/schema";
import { VisitForm } from "./VisitForm";

type InitialVisitFormProps = {
  onSubmit: (data: PersonalInformationFormData) => void;
  isLoading?: boolean;
};

export const InitialVisitForm = ({
  onSubmit,
  isLoading = false,
}: InitialVisitFormProps) => {
  const isNirvanaEnabled = useBookingFlowStore(
    (state) => state.isNirvanaEnabled,
  );
  const result = useNirvanaStore((state) => state.result);
  const demographics = useNirvanaStore((state) => state.demographics);

  const insuranceData = usePatientStore((state) => state.insuranceData);
  const appointmentDetails = usePatientStore(
    (state) => state.appointmentDetails,
  );
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const { getCurrentUser } = useUsers();
  const { data: currentUser } = getCurrentUser(isLoggedIn);
  const hasEhrId = !!currentUser?.ehrId;
  const hasInsurance = !!insuranceData;
  const bypassesNirvanaCheck = !!insuranceData?.bypasses_nirvana_check;
  const usesNirvanaDemographics = isNirvanaEnabled && !bypassesNirvanaCheck;
  const hasNirvanaDemographics = usesNirvanaDemographics && !!demographics;

  const bookingFlow = useBookingFlowStore((state) => state.flow);
  const { mutate: updateBookingFlow } = useUpdateBookingFlowMutation();

  const isSlotWithinThreshold = useMemo(() => {
    if (!appointmentDetails.visitDateTime) return false;
    const slotTime = new Date(appointmentDetails.visitDateTime).getTime();
    return (
      slotTime - Date.now() <
      env.VITE_INSURANCE_REQUIRED_HOURS_THRESHOLD * 60 * 60 * 1000
    );
  }, [appointmentDetails.visitDateTime]);

  const status = result?.status ?? NIRVANA_STATUS.SKIPPED;
  const planIdentified =
    usesNirvanaDemographics &&
    (status === NIRVANA_STATUS.COVERED ||
      status === NIRVANA_STATUS.NOT_COVERED);
  const showInsurance = hasInsurance && !planIdentified;

  const schema = useMemo(
    () => buildInitialVisitSchema(hasEhrId, showInsurance),
    [hasEhrId, showInsurance],
  );

  const handleSubmit = (data: PersonalInformationFormData) => {
    if (bookingFlow && "fillInsuranceInfo" in data && data.fillInsuranceInfo) {
      updateBookingFlow({
        id: bookingFlow.id,
        [BOOKING_FLOW_EVENTS.ENTER_INSURANCE_INFO]: data.fillInsuranceInfo,
      });
    }

    onSubmit(data);
  };

  if (usesNirvanaDemographics && (!result || !demographics)) {
    return <Navigate to={ROUTES.schedulingDemographics} />;
  }

  const initialValues =
    hasNirvanaDemographics && demographics
      ? {
          firstName: demographics.legalFirstName,
          lastName: demographics.legalLastName,
          dateOfBirth: demographics.dob,
          sexAtBirth: demographics.sexAtBirth,
          zipCode: demographics.zipCode,
        }
      : undefined;

  return (
    <VisitForm
      schema={schema}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      initialValues={initialValues}
      fillInsuranceNow={showInsurance && !isSlotWithinThreshold}
      forceInsuranceNow={showInsurance && isSlotWithinThreshold}
    >
      {!hasNirvanaDemographics && !hasEhrId && <VisitForm.GeneralInfo />}
      {!hasEhrId && <VisitForm.ContactInfo />}
      <VisitForm.Address />
      {showInsurance && <VisitForm.OptionalInsurance />}
      {!hasEhrId && <VisitForm.ReferralSource />}
      <VisitForm.Consents />
      <VisitForm.CancellationPolicy />
      <VisitForm.SubmitSection />
    </VisitForm>
  );
};
